<template>
  <div>
    <v-autocomplete
      v-model="trainer"
      :disabled="disabled || isLoading"
      hide-details
      :items="trainers"
      item-value="_id"
      item-text="name"
      outlined
      dense
      background-color="white"
      :label="$t('course.trainer')"
      clearable
      auto-select-first
      :filter="customLocalisedAutoCompleteFilter"
      :search-input.sync="searchInput"
      class="rounded-lg"
    >
      <template v-slot:item="{ item }">
        <div class="d-flex justify-start">
          {{ item.name }}
          {{ item.email ? `(${item.email})` : "" }}
        </div>
      </template>
      <template v-slot:append-item>
        <div v-intersect="onIntersect" v-if="hasMore" class="text-center pa-2">
          <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
export default {
  name: "TrainersDropDown",
  props: {
    value: String,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoadingEmployees: true,
      isLoadingSubRoles: true,
      employees: [],
      subRoles: [],
      searchInput: null,
      debouncedSearchInput: null,
      employeeSkip: 0,
      limit: 50,
      totalCount: 0,
    };
  },
  computed: {
    isLoading() {
      return this.isLoadingEmployees || this.isLoadingSubRoles;
    },
    hasMore() {
      return this.employees.length < this.totalCount;
    },
    trainerId() {
      return this.subRoles.find(x => x.name === "TRAINER")?._id;
    },
    trainers() {
      return this.employees.map(obj => ({
        ...obj,
        name: `${obj?.user?.firstName || ""} ${obj?.user?.lastName || ""}`.trim(),
        email: obj?.user?.email,
      }));
    },
    trainer: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  watch: {
    searchInput(val) {
      if (!val && this.trainer) {
        this.trainer = null;
      }
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        if (this.debouncedSearchInput !== val) {
          this.employeeSkip = 0;
          this.debouncedSearchInput = val;
        }
      }, 500);
    }
  },
  methods: {
    onIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.hasMore && !this.$apollo.queries.getEmployees.loading) {
        this.employeeSkip += this.limit;
        this.$apollo.queries.getEmployees.fetchMore({
          variables: {
            skip: this.employeeSkip,
            limit: this.limit,
            filterInput: {
              subRole: this.trainerId,
              search: this.searchInput || null,
            }
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              getEmployees: {
                __typename: previousResult.getEmployees.__typename,
                totalCount: fetchMoreResult.getEmployees.totalCount,
                employees: [
                  ...previousResult.getEmployees.employees,
                  ...fetchMoreResult.getEmployees.employees,
                ],
              },
            };
          },
        });
      }
    }
  },
  apollo: {
    getEmployees() {
      return {
        query: require("../../user-module/api/query/getEmployees.gql"),
        variables() {
          return {
            limit: this.limit,
            skip: this.employeeSkip,
            filterInput: {
              subRole: this.trainerId,
              search: this.debouncedSearchInput || null,
            }
          };
        },
        skip() {
          return !this.trainerId;
        },
        result({ data, loading }) {
          if (data?.getEmployees) {
            if (this.employeeSkip === 0) {
              this.employees = data.getEmployees.employees;
            } else {
              // FetchMore handles appending, but we can also sync directly just in case
              const existingIds = new Set(this.employees.map(e => e._id));
              const newItems = data.getEmployees.employees.filter(e => !existingIds.has(e._id));
              this.employees = [...this.employees, ...newItems];
            }
            this.totalCount = data.getEmployees.totalCount;
          }
          this.isLoadingEmployees = loading;
        },
      };
    },
    getSubRoles() {
      return {
        query: require("../api/query/getSubRoles.gql"),
        variables: { limit: 0, skip: 0 },
        result({ data, loading }) {
          this.subRoles = data?.getSubRoles?.subRoles ?? [];
          this.isLoadingSubRoles = loading;
        },
      };
    },
  },
};
</script>

<style scoped></style>
